<template>
  <div class="auth-container register-container py-4">
    <img class="logo" alt="Feeds.automated logo" src="/app/images/logo.svg" />
    <p>{{ $t('auth.enter_account_details') }}</p>
    <ValidationObserver ref="observer" v-slot="{ invalid }" class="d-flex flex-column w-50 align-items-center">
      <validation-provider class="mb-4" mode="aggressive" rules="required|email" v-slot="{ errors }">
        <input v-model="email" class="form-control" name="emailAddress" type="email" :placeholder="$t('auth.input_email')">
        <span class="validation-error-required pl-2" v-if="errors[0] === 'emailAddress is not valid.'">{{ $t('validations.email_incorrect') }}</span>
      </validation-provider>
      <validation-provider class="mb-4 d-flex flex-column password-container" mode="lazy" :rules="{
          required: true,
          min_value: 8,
          regex: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/
        }" v-slot="{ errors }" vid="password">
        <input id="password" v-model="password" class="form-control" type="password" :placeholder="$t('auth.input_password')">
        <eye-icon @click="togglePasswordPreview"></eye-icon>
        <span class="validation-error-required pl-2" v-if="errors[0] === 'Pole jest wymagane'">{{ $t('validations.required') }}</span>
        <span class="validation-error-required pl-2" v-if="errors[0] === 'password is not valid.'">{{ $t('validations.password_invalid') }}</span>
      </validation-provider>
      <validation-provider class="mb-4 password-container" mode="lazy" rules="required|confirmed:password" v-slot="{ errors }">
        <input id="confirmPassword" v-model="passwordConfirm" class="form-control" type="password" :placeholder="$t('auth.input_password_repeat')">
        <eye-icon @click="togglePasswordConfirmPreview"></eye-icon>
        <span class="validation-error-required pl-2" v-if="errors[0] === 'Pole jest wymagane'">{{ $t('validations.required') }}</span>
        <span class="validation-error-required pl-2" v-else-if="errors[0] === 'confirmPassword is not valid.'">{{ $t('validations.passwords_not_match') }}</span>
      </validation-provider>
      <validation-provider class="mb-4" mode="lazy" rules="required" v-slot="{ errors }">
        <input v-model="name" class="form-control" name="profileName" type="text" :placeholder="$t('auth.name')">
        <span class="validation-error-required" v-if="errors[0] === 'profileName is not valid.'">{{ $t('validations.required') }}</span>
      </validation-provider>
      <validation-provider class="mb-4" mode="lazy" rules="required" v-slot="{ errors }">
        <input v-model="surname" class="form-control" name="profileSurname" type="text" :placeholder="$t('auth.surname')">
        <span class="validation-error-required" v-if="errors[0] === 'profileSurname is not valid.'">{{ $t('validations.required') }}</span>
      </validation-provider>
      <VuePhoneNumberInput
        @focus="!isCountrySelectorFocused"
        @update="validatePhoneNumber"
        class="mb-4"
        v-model="phone"
        :border-radius="5"
        :color="'#2e343d'"
        :fetch-country="true"
        :no-use-browser-locale="true">
      </VuePhoneNumberInput>
      <div class="mb-4">
        <b-form-checkbox v-model="isChecked" id="registerAcceptTermsCheckbox" class="pl-4 ml-2">
          <label class="form-check-label" v-html="$t('auth.register_agreement', {termsUrl: 'https://feedsautomated.com/regulamin/', renouncementUrl: 'http://feedsautomated.com/odstapienie/', privacyPolicyUrl: 'http://feedsautomated.com/polityka-prywatnosci/'})"></label>
        </b-form-checkbox>
      </div>
      <button type="submit" class="btn btn-large btn-orange" @click="register" :disabled="invalid || !isChecked || !isPhoneNumberValid">{{ $t('auth.register_send_activation_link') }}</button>
      <router-link :to="{ name: 'auth.login'}" class="my-3">
        <button type="button" class="btn btn-large btn-orange">{{ $t('auth.back_to_login') }}</button>
      </router-link>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { required, email, confirmed, regex, min } from 'vee-validate/dist/rules'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import axios from 'axios'
import { EyeIcon } from 'vue-feather-icons'

extend('required', {
  ...required
})

extend('email', {
  ...email
})

extend('confirmed', {
  ...confirmed
})

extend('regex', {
  ...regex
})

extend('min_value', {
  ...min
})

export default {
  name: 'Registration',

  data () {
    return {
      email: null,
      formattedPhoneNumber: null,
      isChecked: false,
      isCountrySelectorFocused: false,
      isPhoneNumberValid: false,
      name: null,
      password: null,
      passwordConfirm: null,
      phone: null,
      surname: null
    }
  },

  created () {
    this.$i18n.locale = navigator.language || navigator.userLanguage
    this.$cookies.set('language_version', navigator.language.substr(0, 2) || navigator.userLanguage.substr(0, 2))
    document.addEventListener('focusin', this.focusChanged)
  },

  mounted () {
    window.addEventListener('keypress', (e) => {
      if (e.keyCode === 13) {
        this.register()
      }
    })
  },

  beforeDestroy () {
    document.removeEventListener('focusin', this.focusChanged)
  },

  methods: {
    focusChanged (event) {
      const el = event.target
      if (el.classList[0] === 'country-selector__input') this.isCountrySelectorFocused = true
      else this.isCountrySelectorFocused = false
    },

    register () {
      if (this.$refs.observer !== undefined) {
        if ((this.$refs.observer.flags.valid) && (this.isPhoneNumberValid) && (this.isChecked) && (!this.isCountrySelectorFocused)) {
          axios.post(process.env.VUE_APP_API_ENDPOINT + '/auth/register', {
            email: this.email,
            password: this.password,
            language: this.$cookies.get('language_version'),
            userData: {
              name: this.name,
              surname: this.surname,
              phoneNumber: this.formattedPhoneNumber
            }
          })
            .then((response) => {
              if ((response.status === 201) || (response.status === 200)) {
                this.$router.push({ name: 'auth.account-inactive', params: { emailAddress: this.email } })
              }
            })
            .catch((error) => {
              if (error.response) {
                const h = this.$createElement
                const message = h(
                  'div',
                  [
                    h('p', this.$t('auth.registration_unsuccessful')),
                    h('p', [
                      h('strong', this.$t('global.error_code')),
                      h('span', error.response.data.statusCode)
                    ]),
                    h('p', [
                      h('strong', this.$t('global.error_status')),
                      h('span', error.response.data.message)
                    ])
                  ]
                )
                this.$bvToast.toast([message], {
                  autoHideDelay: 3000,
                  solid: true,
                  title: this.$t('auth.registration_error'),
                  variant: 'danger'
                })
              }
            })
        }
      }
    },

    validatePhoneNumber (value) {
      this.formattedPhoneNumber = value.formattedNumber
      this.isPhoneNumberValid = value.isValid
    },

    togglePasswordPreview () {
      const passwordInput = document.getElementById('password')
      if (passwordInput.type === 'password') passwordInput.type = 'text'
      else passwordInput.type = 'password'
    },

    togglePasswordConfirmPreview () {
      const passwordInput = document.getElementById('confirmPassword')
      if (passwordInput.type === 'password') passwordInput.type = 'text'
      else passwordInput.type = 'password'
    }
  },

  components: { EyeIcon, ValidationObserver, ValidationProvider, VuePhoneNumberInput }
}
</script>
